import React, { useState } from 'react';
import { Badge, Drawer, Flex, Segmented, Spin } from 'antd';
import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Button } from '@bettercomp/ui-components';
import { AppThemeProvider } from 'components/AppThemeProvider';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMarkNotificationUnread,
  useNotificationsList,
  useNotificationsSSE,
  useUnreadCount,
  type NotificationsTab,
} from 'service/notificationsService';
import { NotificationErrorBoundary } from './NotificationErrorBoundary';
import { renderNotification } from './registry/NotificationRegistry';
import { NotificationShell } from './NotificationShell';

export function NotificationsDrawer() {
  useNotificationsSSE();
  const [open, setOpen] = useState(false);
  const { data: unreadCount = 0 } = useUnreadCount();
  const { mutate: markAllRead, isPending: isMarkingAllRead } = useMarkAllNotificationsRead();

  return (
    <>
      <Badge size="small" count={unreadCount} data-testid="notifications-drawer-badge">
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          onClick={() => setOpen(true)}
          style={{ background: 'transparent' }}
          data-testid="notifications-drawer-trigger"
        />
      </Badge>
      <AppThemeProvider>
        <Drawer
          title={
            <Flex justify="space-between" align="center">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <Button size="small" type="link" loading={isMarkingAllRead} onClick={() => markAllRead()}>
                  Mark all read
                </Button>
              )}
            </Flex>
          }
          placement="right"
          open={open}
          onClose={() => setOpen(false)}
          destroyOnHidden
          data-testid="notifications-drawer"
        >
          {/* Mounted only while the drawer is open (destroyOnHidden): the tab list
              queries exist exactly when their data is on screen, so SSE-driven
              invalidations refetch nothing for closed drawers, and gcTime: 0
              drops the caches on close — every open starts from a fresh fetch. */}
          <NotificationsDrawerContent initialTab={unreadCount > 0 ? 'unread' : 'all'} unreadCount={unreadCount} />
        </Drawer>
      </AppThemeProvider>
    </>
  );
}

type ContentProps = {
  initialTab: NotificationsTab;
  unreadCount: number;
};

function NotificationsDrawerContent({ initialTab, unreadCount }: ContentProps) {
  const [tab, setTab] = useState<NotificationsTab>(initialTab);
  const {
    data: notifications = [],
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useNotificationsList(tab);
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markUnread } = useMarkNotificationUnread();

  return (
    <>
      <Segmented<NotificationsTab>
        block
        value={tab}
        onChange={setTab}
        options={[
          { label: `Unread (${unreadCount})`, value: 'unread' },
          { label: 'All', value: 'all' },
        ]}
        style={{ marginBottom: 12 }}
      />
      {isLoading && (
        <Flex justify="center" align="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      )}
      {!isLoading && notifications.length === 0 && (
        <Flex vertical align="center" justify="center" gap={8} style={{ padding: 24, textAlign: 'center' }}>
          <CheckCircleOutlined style={{ fontSize: 48 }} />
          <span>You&apos;re all caught up</span>
          <span>
            No new notifications. We&apos;ll let you know when reports, exports or reviews are ready.
          </span>
        </Flex>
      )}
      {!isLoading &&
        notifications.map((notification) => {
          const content = renderNotification(notification);
          if (!content) { return null; }
          return (
            <NotificationErrorBoundary key={notification.id}>
              <NotificationShell
                notification={notification}
                onMarkRead={markRead}
                onMarkUnread={markUnread}
                content={content}
              />
            </NotificationErrorBoundary>
          );
        })}
      {!isLoading && hasNextPage && (
        <Flex justify="center" style={{ marginTop: 12 }}>
          <Button
            type="link"
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            data-testid="notifications-load-more"
          >
            Load more
          </Button>
        </Flex>
      )}
    </>
  );
}
